/**
 * ScheduledTable — CRUD-style table for scheduled reports.
 *
 * Follows AreasPage/AlertDetailsTable patterns with kebab menu actions:
 * Edit Report, Send Report, Delete Report.
 */

import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';
import FilterPopover from '../../../community/residents/components/FilterPopover.jsx';
import ActiveFilterBar from '../../../community/residents/components/ActiveFilterBar.jsx';

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

export const COLUMN_DEFS = [
    { key: 'description', label: 'Report Description', sortable: true, filterable: true, required: true },
    { key: 'report_type_name', label: 'Report Type', sortable: true, filterable: true },
    { key: 'frequency_name', label: 'Frequency', sortable: true, filterable: true },
    { key: 'send_report_time', label: 'Send Time', sortable: true, filterable: false },
    { key: 'report_interval_name', label: 'Duration', sortable: true, filterable: true },
    { key: 'last_sent', label: 'Last Sent', sortable: true, filterable: false },
    { key: 'next_send_time', label: 'Next Send', sortable: true, filterable: false },
    { key: 'report_receiver', label: 'Receivers', sortable: false, filterable: false },
    { key: 'report_user_ids', label: 'Users', sortable: false, filterable: false },
];

const COLUMN_LABELS = {};
COLUMN_DEFS.forEach((col) => { COLUMN_LABELS[col.key] = col.label; });

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatSecondsToTime(seconds) {
    if (seconds == null || seconds === '') return '--';
    const totalSec = parseInt(seconds, 10);
    if (isNaN(totalSec)) return '--';
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const period = h >= 12 ? 'PM' : 'AM';
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${displayH}:${String(m).padStart(2, '0')} ${period}`;
}

function formatDatetimeUtc(val, tzOffset) {
    if (!val) return '--';
    try {
        return convertIsoUTCToLocal(val, tzOffset) || val;
    } catch { return val; }
}

// ---------------------------------------------------------------------------
// RowActions (kebab menu)
// ---------------------------------------------------------------------------

const RowActions = ({ row, onEdit, onSend, onRunLocal, onDelete, canManageReports = true }) => {
    const [open, setOpen] = useState(false);
    const [menuStyle, setMenuStyle] = useState({});
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    const handleToggle = (e) => {
        e.stopPropagation();
        if (!open && menuRef.current) {
            const rect = menuRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            if (spaceBelow < 180) {
                setMenuStyle({ position: 'fixed', bottom: `${window.innerHeight - rect.top + 4}px`, right: `${window.innerWidth - rect.right}px`, top: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            } else {
                setMenuStyle({ position: 'fixed', top: `${rect.bottom + 4}px`, right: `${window.innerWidth - rect.right}px`, bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999 });
            }
        }
        setOpen(!open);
    };

    return (
        <div className="ap-row-actions" ref={menuRef}>
            <button type="button" className="ap-row-actions__trigger" onClick={handleToggle} aria-label="Actions" aria-expanded={open}>
                <i className="fe fe-more-vertical" aria-hidden="true" />
            </button>
            {open ? (
                <div className="ap-row-actions__menu" style={menuStyle}>
                    <button className="ap-row-actions__item" onClick={() => { setOpen(false); onEdit(row); }}>
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                        </svg>
                        Edit Report
                    </button>
                    <button className="ap-row-actions__item" onClick={() => { setOpen(false); onSend(row); }}>
                        <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                        </svg>
                        Send Emailed Report
                    </button>
                    {onRunLocal ? (
                        <button className="ap-row-actions__item" onClick={() => { setOpen(false); onRunLocal(row); }}>
                            <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
                            </svg>
                            Run Report Local
                        </button>
                    ) : null}
                    {canManageReports ? (
                        <button className="ap-row-actions__item ap-row-actions__item--danger" onClick={() => { setOpen(false); onDelete(row); }}>
                            <svg className="ap-icon-md ap-row-actions__item-icon" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                            </svg>
                            Delete Report
                        </button>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// ReceiversPopoverCell — inline popover showing email recipients
// ---------------------------------------------------------------------------

/** Compute fixed popover position: centered on trigger, clamped to viewport edges */
function computePopoverPosition(triggerRef, popoverWidth = 256) {
    if (!triggerRef.current) return {};
    const rect = triggerRef.current.getBoundingClientRect();
    const pad = 8; // min distance from viewport edge
    const spaceBelow = window.innerHeight - rect.bottom;

    // Center horizontally on the badge, then clamp to viewport
    let left = rect.left + rect.width / 2 - popoverWidth / 2;
    left = Math.max(pad, Math.min(left, window.innerWidth - popoverWidth - pad));

    const base = { position: 'fixed', left: `${left}px`, margin: 0, zIndex: 9999, transform: 'none', right: 'auto' };

    if (spaceBelow < 200) {
        return { ...base, bottom: `${window.innerHeight - rect.top + 4}px`, top: 'auto' };
    }
    return { ...base, top: `${rect.bottom + 4}px`, bottom: 'auto' };
}

const ReceiversPopoverCell = ({ emails }) => {
    const [open, setOpen] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState({});
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    const computePosition = () => {
        setPopoverStyle(computePopoverPosition(ref));
    };

    const handleOpen = (nextOpen) => {
        if (nextOpen) computePosition();
        setOpen(nextOpen);
    };

    return (
        <div className="ap-device-cell" ref={ref}>
            <button
                type="button"
                className="ap-device-badge"
                onClick={() => handleOpen(!open)}
                onMouseEnter={() => handleOpen(true)}
                onMouseLeave={() => handleOpen(false)}
                aria-expanded={open}
                aria-label={`${emails.length} recipient${emails.length !== 1 ? 's' : ''}`}
            >
                <i className="fe fe-mail" style={{ fontSize: '12px' }} aria-hidden="true" />
                {emails.length}
            </button>
            {open ? (
                <div className="ap-device-popover" style={popoverStyle}>
                    <div className="ap-device-popover__title">Recipients ({emails.length})</div>
                    <ul className="ap-device-popover__list">
                        {emails.map((email, i) => (
                            <li key={i} className="ap-device-popover__item">{email}</li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// UsersPopoverCell — badge count with hover popover listing user names/emails
// ---------------------------------------------------------------------------

const UsersPopoverCell = ({ userEntries }) => {
    const [open, setOpen] = useState(false);
    const [popoverStyle, setPopoverStyle] = useState({});
    const ref = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        if (open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onScroll = () => setOpen(false);
        window.addEventListener('scroll', onScroll, true);
        return () => window.removeEventListener('scroll', onScroll, true);
    }, [open]);

    const computePosition = () => {
        setPopoverStyle(computePopoverPosition(ref));
    };

    const handleOpen = (nextOpen) => {
        if (nextOpen) computePosition();
        setOpen(nextOpen);
    };

    return (
        <div className="ap-device-cell" ref={ref}>
            <button
                type="button"
                className="ap-device-badge"
                onClick={() => handleOpen(!open)}
                onMouseEnter={() => handleOpen(true)}
                onMouseLeave={() => handleOpen(false)}
                aria-expanded={open}
                aria-label={`${userEntries.length} user${userEntries.length !== 1 ? 's' : ''}`}
            >
                <i className="fe fe-users" style={{ fontSize: '12px' }} aria-hidden="true" />
                {userEntries.length}
            </button>
            {open ? (
                <div className="ap-device-popover" style={popoverStyle}>
                    <div className="ap-device-popover__title">Users ({userEntries.length})</div>
                    <ul className="ap-device-popover__list">
                        {userEntries.map((u, i) => (
                            <li key={i} className="ap-device-popover__item" style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                <span style={{ fontWeight: 500 }}>{u.name}</span>
                                {u.email ? (
                                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)' }}>{u.email}</span>
                                ) : null}
                            </li>
                        ))}
                    </ul>
                </div>
            ) : null}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Cell renderer
// ---------------------------------------------------------------------------

function renderCell(row, colKey, tzOffset, usersLookup) {
    const val = row[colKey];
    switch (colKey) {
        case 'report_type_name':
            return val ? <span style={{ textTransform: 'capitalize' }}>{val}</span> : '--';
        case 'send_report_time':
            return formatSecondsToTime(val);
        case 'last_sent':
        case 'next_send_time':
            return formatDatetimeUtc(val, tzOffset);
        case 'report_receiver': {
            if (!val) return '--';
            const emails = val.split(',').map((e) => e.trim()).filter(Boolean);
            if (emails.length === 0) return '--';
            return <ReceiversPopoverCell emails={emails} />;
        }
        case 'report_user_ids': {
            if (!val) return '--';
            const ids = String(val).split(',').map((id) => id.trim()).filter(Boolean);
            if (ids.length === 0) return '--';
            const entries = ids.map((id) => {
                const u = usersLookup?.[id];
                return u ? { name: u.display_name || '(unknown)', email: u.email || '' } : { name: `User #${id}`, email: '' };
            });
            return <UsersPopoverCell userEntries={entries} />;
        }
        default:
            return val != null ? String(val) : '--';
    }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ScheduledTable = ({
    data, allData, visibleColumns, sortColumn, sortOrder, onSort,
    filters, onFilterChange, onClearFilters,
    selectedIds, onSelectRow, onSelectAll,
    onEdit, onSend, onRunLocal, onDelete, tzOffset = 0, users = [],
    canManageReports = true,
}) => {
    const activeCols = COLUMN_DEFS.filter((col) => visibleColumns.includes(col.key));

    // Build user lookup by user_id for popover rendering
    const usersLookup = useMemo(() => {
        const map = {};
        (users || []).forEach((u) => { map[String(u.user_id)] = u; });
        return map;
    }, [users]);

    const filterValues = useMemo(() => {
        const vals = {};
        COLUMN_DEFS.filter((c) => c.filterable).forEach((col) => {
            const unique = [...new Set((allData || []).map((r) => r[col.key]).filter((v) => v != null && v !== ''))].sort();
            vals[col.key] = unique;
        });
        return vals;
    }, [allData]);

    const handleRemoveFilter = useCallback((columnKey, value) => {
        const current = filters[columnKey] || [];
        onFilterChange(columnKey, current.filter((v) => v !== value));
    }, [filters, onFilterChange]);

    const allSelected = data.length > 0 && data.every((row) => selectedIds.includes(row.report_id));
    const someSelected = data.some((row) => selectedIds.includes(row.report_id));

    return (
        <div className="ap-table-container">
            <ActiveFilterBar
                filters={filters}
                columnLabels={COLUMN_LABELS}
                onRemoveFilter={handleRemoveFilter}
                onClearAll={onClearFilters}
            />
            <div className="dp-table-wrap">
                <table className="dp-table">
                    <thead className="dp-thead">
                        <tr>
                            <th scope="col" className="ap-th ap-th--checkbox">
                                <input
                                    type="checkbox"
                                    className="ap-checkbox"
                                    checked={allSelected}
                                    ref={(el) => { if (el) el.indeterminate = someSelected && !allSelected; }}
                                    onChange={onSelectAll}
                                    aria-label="Select all"
                                />
                            </th>
                            <th scope="col" className="ap-th ap-th--actions">
                                <span className="ap-sr-only">Actions</span>
                            </th>
                            {activeCols.map((col) => (
                                <th key={col.key} scope="col" className="ap-th">
                                    <div className="ap-th__content">
                                        {col.sortable ? (
                                            <button type="button" onClick={() => onSort(col.key)} className="ap-th__sort-btn" aria-label={`Sort by ${col.label}`}>
                                                {col.label}
                                                {sortColumn === col.key ? (
                                                    <svg className="ap-icon-sm" style={{ marginLeft: '2px', flexShrink: 0 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                                                        {sortOrder === 'asc'
                                                            ? <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                                                            : <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />}
                                                    </svg>
                                                ) : null}
                                            </button>
                                        ) : (<span>{col.label}</span>)}
                                        {col.filterable ? (
                                            <FilterPopover
                                                columnKey={col.key}
                                                label={col.label}
                                                values={filterValues[col.key] || []}
                                                selectedValues={filters[col.key] || []}
                                                onChange={onFilterChange}
                                            />
                                        ) : null}
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 ? (
                            <tr className="dp-tr">
                                <td className="dp-td" colSpan={activeCols.length + 2} style={{ textAlign: 'center', padding: 'var(--space-8)' }}>
                                    <span className="dp-cell-text dp-cell-text--muted">No scheduled reports found.</span>
                                </td>
                            </tr>
                        ) : null}
                        {data.map((row) => {
                            const isSelected = selectedIds.includes(row.report_id);
                            return (
                                <tr key={row.report_id} className={`dp-tr${isSelected ? ' is-selected' : ''}`}>
                                    <td className="dp-td dp-td--checkbox">
                                        <input
                                            type="checkbox"
                                            className="ap-checkbox"
                                            checked={isSelected}
                                            onChange={() => onSelectRow(row.report_id)}
                                        />
                                    </td>
                                    <td className="dp-td" style={{ textAlign: 'center' }}>
                                        <RowActions row={row} onEdit={onEdit} onSend={onSend} onRunLocal={onRunLocal} onDelete={onDelete} canManageReports={canManageReports} />
                                    </td>
                                    {activeCols.map((col) => (
                                        <td key={col.key} className="dp-td">{renderCell(row, col.key, tzOffset, usersLookup)}</td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ScheduledTable;
